import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import { Entity } from '@adornis/baseql/decorators.js';
import { registerQuery } from '@adornis/baseql/metadata/register.js';
import { getAllEntities, getByID } from '@adornis/baseql/operations/mongo.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { getMenteeUserClass, getMentorUserClass, getUserClasses } from '@adornis/bh-matching-matches/user-class.js';
import { type BuddyUser } from '@adornis/bh-matching-user/entities/buddy-user.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { ExportDataView } from '@adornis/data-view/db/export-data-view.js';
import { MailTemplate } from '@adornis/mails/db/mail-template.js';
import type { BuildEmailOptions } from '@adornis/mails/db/templates.js';
import { html } from 'lit';
import { ensureDefaultBuddyMailTemplates } from '../api/ensure-mail-templates.js';
import { BuddyMailAttachment } from './attachment.js';

export enum MailTemplateType {
  MATCHING_MENTOR = 'Matching_Mentor',
  MATCHING_MENTEE = 'Matching_Mentee',
  CONTACT_REMINDER_MENTOR = 'Contact_Reminder_Mentor',
  CONTACT_REMINDER_MENTEE = 'Contact_Reminder_Mentee',
  REGISTRATION_MENTOR = 'Registration_Mentor',
  REGISTRATION_MENTEE = 'Registration_Mentee',
  FREE_FORM = 'Free_Form',
}

export enum MailTemplateVars {
  SALUTATION = 'salutation',
}

@Entity()
export class BuddyMailTemplate extends MailTemplate {
  static override _class = 'BuddyMailTemplate';

  static override buildMailOptions: Partial<BuildEmailOptions> = {
    title: 'Buchungsheld Benachrichtigungsmail',
    footer: html`
      <div class="section" style="margin-top: 20px; font-size: 0.8em;">
        <div style="width: 100%">
          <div><a style="text-decoration: none;" href=${A.absoluteUrl('/imprint')}>Impressum</a></div>
          <div><a style="text-decoration: none;" href=${A.absoluteUrl('/data-privacy')}>Datenschutz</a></div>
        </div>
      </div>
    `,
    colorConfig: {
      mainBorder: 'none',
      mainBack: DesignSystem.currentTheme.getValue().colors.neutral.shade(50),
      wrapperBack: 'transparent',
    },
  };

  static override async getAvailableAttachments(emailType: MailTemplateType) {
    // every attachment is available for every mail type here

    const attachments = await getAllEntities(BuddyMailAttachment)({ _id: 1 });
    return attachments.map(attachment => attachment._id);
  }

  protected ensureAndGetMailTemplates() {}
}

export const getBuddyMailTemplates = registerQuery({
  operationName: 'getBuddyMailTemplates',
  resolve:
    () =>
    async <SelSet extends BaseQLSelectionSet<BuddyMailTemplate>>(gqlFields: SelSet) => {
      await ensureDefaultBuddyMailTemplates();

      const existingBuddyMailTemplates = await (
        await getRawCollection<BuddyMailTemplate>(BuddyMailTemplate._collectionName)
      )
        .find({ _class: BuddyMailTemplate._class })
        .toArray();

      return existingBuddyMailTemplates;
    },
  type: () => [BuddyMailTemplate],
});

export function convertUserToMailVarObject(user: BuddyUser, prefix?: string) {
  const prefixed = !!prefix ? `${prefix}.` : '';

  const vars = Object.fromEntries([
    ...Object.entries(user.toJSON()).map(([key, value]) => {
      return [`${prefixed}${key}`, optionallyTranslateEnus(value)];
    }),

    ...(prefix === 'mentor'
      ? getMentorUserClass().getMailVariables(user)
      : getMenteeUserClass().getMailVariables(user)
    ).map(([key, value]) => [`${prefixed}${key}`, optionallyTranslateEnus(value)]),
  ]);

  return vars;
}

function optionallyTranslateEnus(value: string) {
  //in hs-fulda, dictionary.enums didn't contain the values we tried to access. In that case we return the value itself
  return ExportDataView._translator?._options?.dictionary?.enums?.[value]?.[0] ?? value;
}

export const getMentorFields = () => {
  const filter = (x: string) =>
    !x.startsWith('_') &&
    !['ios_sub', 'username', 'roles', 'isOnline', 'createdAt', 'emailConfirmed', 'timeframeID', 'matches'].includes(x);

  return Object.keys(getMentorUserClass().fields).filter(filter);
};

export const getMenteeFields = () => {
  const filter = (x: string) =>
    !x.startsWith('_') &&
    !['ios_sub', 'username', 'roles', 'isOnline', 'createdAt', 'emailConfirmed', 'timeframeID', 'matches'].includes(x);

  return Object.keys(getMenteeUserClass().fields).filter(filter);
};

export const getMentorAndMenteeMailVariables = () => {
  return [
    `mentor.${MailTemplateVars.SALUTATION}`,
    ...getMentorFields().map(fieldKey => `mentor.${fieldKey}`),
    `mentee.${MailTemplateVars.SALUTATION}`,
    ...getMenteeFields().map(fieldKey => `mentee.${fieldKey}`),
  ];
};

export async function getMentorAndMenteeMailVariableData(matchID: string, emailType: MailTemplateType) {
  const match = await getByID(Match, matchID)({ _id: 1, mentorID: 1, menteeID: 1 });
  if (!match) throw new Error('No match found for ID ' + matchID);

  const userClasses = getUserClasses();
  if (!match.mentorID || !match.menteeID)
    throw new Error('match IDs missing, this state should not be allowed to exist at this point');
  const mentor = await getByID(userClasses.mentor, match.mentorID)(selectionSet(() => userClasses.mentor, 2));
  const mentee = await getByID(userClasses.mentee, match.menteeID)(selectionSet(() => userClasses.mentee, 2));

  if (!mentor || !mentee) {
    logger.error(
      { mentor: !!mentor, mentee: !!mentee, matchID: match._id },
      'Couldnt find mentor or mentee for match mail',
    );

    throw new Error('Couldnt find mentor or mentee for match mail');
  }

  const data = {
    ...convertUserToMailVarObject(mentor, 'mentor'),
    ...convertUserToMailVarObject(mentee, 'mentee'),
  };

  // attachments
  const mailTemplate = await loadMongoData(BuddyMailTemplate, { emailType }).then(res => res?.[0]);
  if (!mailTemplate) throw new Error('No mail template found for type ' + emailType);

  const attachments = {};
  const attachmentData = await loadMongoData(BuddyMailAttachment, { _id: { $in: mailTemplate.activatedAttachments } });
  for (const attachment of attachmentData) {
    attachments[attachment._id] = `${BuddyMailAttachment.getServeLink(attachment._id)}/${
      attachment.meta?.fileName ?? 'file'
    }.${attachment.meta?.extension ?? 'txt'}`;
  }

  return { data, attachments, scope: 'default' };
}
